import type { User } from "firebase/auth";
import { CHARACTER_ACTIONS } from "@/character/actions";
import { GRID_COLUMNS, SHEET_COLUMNS } from "@/character/grid";
import { mountCharacterGridShowcase } from "@/character/grid-player";
import type { CharacterProfile } from "@/character/storage";
import { GameEvents, type GameModeChangedPayload } from "@/game/events/game-events";
import { loadGameAccessMode, userCanAccessGmMode, type GameAccessMode } from "@/game/game-mode";
import { permissionsForGameMode } from "@/game/game-mode-permissions";
import { mountGameModeControl, type GameModeControlHandle } from "@/game/mount-game-mode-control";
import { grantExpForUser } from "@/game/model/grant-exp";

function escapeHtml(text: string): string {
  return text
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

export type CharacterSpritesheetTabHandle = {
  teardown: () => void;
};

export function mountCharacterSpritesheetTab(
  host: HTMLElement,
  user: User,
  profile: CharacterProfile,
): CharacterSpritesheetTabHandle {
  let stopGridShowcase: (() => void) | undefined;
  let gameModeControl: GameModeControlHandle | undefined;
  let onGameModeChanged: ((event: Event) => void) | undefined;
  const canGm = userCanAccessGmMode(user);
  let gameMode: GameAccessMode = loadGameAccessMode(user.uid, canGm);
  let perms = permissionsForGameMode(gameMode, canGm);

  const actionHints = CHARACTER_ACTIONS
    .map(
      (action) =>
        `<li><strong>${escapeHtml(action.templateLabel)}</strong> — แถว ${action.row + 1}</li>`,
    )
    .join("");

  host.innerHTML = `
    <div class="char-sheet-sprite-tab">
      <div id="char-sheet-sprite-mode-host" class="character-game-mode-host"></div>
      <p class="hint char-sheet-sprite-lede">
        แต่ละแถว = 1 การเคลื่อนไหว · ${GRID_COLUMNS} เฟรมวน loop (อัปโหลดกริด ${SHEET_COLUMNS} คอลัมน์ · บันทึกแล้ว crop เหลือ ${GRID_COLUMNS})
      </p>
      <ul class="action-row-list character-action-map">${actionHints}</ul>
      <div id="char-sheet-sprite-gm-tools" class="character-gm-tools" hidden>
        <label class="field">
          <span class="field-label">GM — ให้ EXP</span>
          <div class="gm-tools-row">
            <input type="number" id="char-sheet-sprite-gm-exp" class="input" min="1" value="1000" />
            <button type="button" class="btn btn-primary btn-xs" id="char-sheet-sprite-gm-exp-btn">ให้</button>
          </div>
        </label>
      </div>
      <div id="char-sheet-sprite-grid-host"></div>
      <p class="char-sheet-sprite-actions">
        <a class="btn btn-ghost btn-xs" href="/character/create" data-gm-only-recreate hidden>สร้างใหม่ (แทนที่)</a>
      </p>
    </div>
  `;

  const syncModeUi = () => {
    perms = permissionsForGameMode(gameMode, canGm);
    const recreate = host.querySelector<HTMLElement>("[data-gm-only-recreate]");
    if (recreate) recreate.hidden = !perms.canRecreateCharacter;
    const gmTools = host.querySelector<HTMLElement>("#char-sheet-sprite-gm-tools");
    if (gmTools) gmTools.hidden = !perms.canGrantExp;
  };

  const modeHost = host.querySelector<HTMLElement>("#char-sheet-sprite-mode-host");
  if (modeHost) {
    gameModeControl = mountGameModeControl(modeHost, user);
    gameMode = gameModeControl.getMode();
  }

  host.querySelector("#char-sheet-sprite-gm-exp-btn")?.addEventListener("click", () => {
    const input = host.querySelector<HTMLInputElement>("#char-sheet-sprite-gm-exp");
    const amount = Math.floor(Number(input?.value ?? 0));
    if (amount <= 0) return;
    grantExpForUser(user.uid, amount);
  });

  onGameModeChanged = (event: Event) => {
    const detail = (event as CustomEvent<GameModeChangedPayload>).detail;
    if (detail.userId !== user.uid) return;
    gameMode = detail.mode;
    syncModeUi();
  };
  window.addEventListener(GameEvents.GameModeChanged, onGameModeChanged);

  const gridHost = host.querySelector<HTMLElement>("#char-sheet-sprite-grid-host");
  if (gridHost) {
    stopGridShowcase = mountCharacterGridShowcase(gridHost, profile);
  }
  syncModeUi();

  return {
    teardown: () => {
      if (onGameModeChanged) {
        window.removeEventListener(GameEvents.GameModeChanged, onGameModeChanged);
      }
      gameModeControl?.teardown();
      stopGridShowcase?.();
    },
  };
}
